import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChartBar, Tray, UsersThree, Briefcase, Article, SignOut, Trash, Plus, PencilSimple, X, AddressBook, UploadSimple, Buildings,
} from "@phosphor-icons/react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import SEO from "../../components/SEO";

const TABS = [
  { id: "overview", label: "Overview", i: ChartBar },
  { id: "leads", label: "Leads", i: Tray },
  { id: "applications", label: "Applications", i: UsersThree },
  { id: "jobs", label: "Jobs", i: Briefcase },
  { id: "insights", label: "Insights", i: Article },
  { id: "team", label: "Team", i: AddressBook },
  { id: "clients", label: "Clients", i: Buildings },
];

function fmt(d) { try { return new Date(d).toLocaleString("en-GB"); } catch { return d; } }

/* ---------- Overview ---------- */
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="font-mono-ez text-sm text-ez-text/50">Loading…</div>;
  const cards = [
    ["Total Leads", stats.leads], ["New Leads", stats.new_leads], ["Applications", stats.applications],
    ["Jobs", stats.jobs], ["Open Jobs", stats.open_jobs], ["Insights", stats.insights],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 border border-ez-text" data-testid="admin-stats">
      {cards.map(([l, v], i) => (
        <div key={i} className="border-b border-r border-ez-border p-8">
          <div className="font-mono-ez text-xs uppercase tracking-widest text-ez-text/40">{l}</div>
          <div className="font-display text-5xl tracking-tighter mt-3">{v}</div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Leads ---------- */
function Leads() {
  const [leads, setLeads] = useState([]);
  const load = useCallback(() => api.get("/admin/leads").then((r) => setLeads(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { await api.delete(`/admin/leads/${id}`); toast.success("Deleted"); load(); };
  const mark = async (id, status) => { await api.patch(`/admin/leads/${id}`, { status }); load(); };

  return (
    <div className="border border-ez-text overflow-x-auto" data-testid="admin-leads">
      <table className="w-full text-sm">
        <thead className="bg-ez-text text-ez-bg font-mono-ez text-xs uppercase tracking-wider">
          <tr>{["Type", "Name", "Email", "Company", "Status", "Date", ""].map((h) => <th key={h} className="text-left p-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} className="border-b border-ez-border">
              <td className="p-3"><span className="bg-ez-yellow px-2 py-0.5 text-xs">{l.lead_type}</span></td>
              <td className="p-3 font-medium">{l.name}</td>
              <td className="p-3">{l.email}</td>
              <td className="p-3">{l.company || "—"}</td>
              <td className="p-3">
                <select value={l.status} onChange={(e) => mark(l.id, e.target.value)} className="border border-ez-border px-2 py-1 text-xs">
                  <option value="new">new</option><option value="contacted">contacted</option><option value="closed">closed</option>
                </select>
              </td>
              <td className="p-3 text-ez-text/50 text-xs">{fmt(l.created_at)}</td>
              <td className="p-3"><button onClick={() => del(l.id)} data-testid={`del-lead-${l.id}`} className="text-red-500 hover:text-red-700"><Trash size={16} /></button></td>
            </tr>
          ))}
          {leads.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-ez-text/50">No leads yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Applications ---------- */
function Applications() {
  const [apps, setApps] = useState([]);
  useEffect(() => { api.get("/admin/applications").then((r) => setApps(r.data)); }, []);
  return (
    <div className="border border-ez-text overflow-x-auto" data-testid="admin-applications">
      <table className="w-full text-sm">
        <thead className="bg-ez-text text-ez-bg font-mono-ez text-xs uppercase tracking-wider">
          <tr>{["Position", "Name", "Email", "Phone", "Resume", "Date"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {apps.map((a) => (
            <tr key={a.id} className="border-b border-ez-border">
              <td className="p-3 font-medium">{a.job_title}</td>
              <td className="p-3">{a.name}</td>
              <td className="p-3">{a.email}</td>
              <td className="p-3">{a.phone || "—"}</td>
              <td className="p-3">{a.resume_url ? <a href={a.resume_url} target="_blank" rel="noreferrer" className="underline">link</a> : "—"}</td>
              <td className="p-3 text-ez-text/50 text-xs">{fmt(a.created_at)}</td>
            </tr>
          ))}
          {apps.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-ez-text/50">No applications yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Jobs CRUD ---------- */
const emptyJob = { title: "", department: "", location: "", employment_type: "Full-time", summary: "", responsibilities: "", requirements: "", is_open: true };
function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/jobs?all=true").then((r) => setJobs(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      responsibilities: (editing.responsibilities || "").split("\n").map((s) => s.trim()).filter(Boolean),
      requirements: (editing.requirements || "").split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing.id) await api.put(`/admin/jobs/${editing.id}`, payload);
      else await api.post("/admin/jobs", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/jobs/${id}`); toast.success("Deleted"); load(); };
  const edit = (j) => setEditing({ ...j, responsibilities: (j.responsibilities || []).join("\n"), requirements: (j.requirements || []).join("\n") });

  return (
    <div data-testid="admin-jobs">
      <button onClick={() => setEditing({ ...emptyJob })} data-testid="new-job-btn" className="mb-6 inline-flex items-center gap-2 bg-ez-text text-ez-bg px-5 py-2.5 font-semibold text-sm hover:bg-ez-yellow hover:text-ez-text transition-colors"><Plus size={16} weight="bold" /> New Job</button>
      <div className="border border-ez-text divide-y divide-ez-border">
        {jobs.map((j) => (
          <div key={j.id} className="p-5 flex items-center justify-between gap-4">
            <div>
              <div className="font-semibold">{j.title} {!j.is_open && <span className="text-xs text-ez-text/40">(closed)</span>}</div>
              <div className="text-xs text-ez-text/50">{j.department} · {j.location}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => edit(j)} className="p-2 hover:bg-ez-bg"><PencilSimple size={16} /></button>
              <button onClick={() => del(j.id)} data-testid={`del-job-${j.id}`} className="p-2 text-red-500 hover:bg-ez-bg"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title={editing.id ? "Edit Job" : "New Job"} onClose={() => setEditing(null)} onSubmit={save} testid="job-form">
          <Field label="Title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Department *" value={editing.department} onChange={(v) => setEditing({ ...editing, department: v })} />
            <Field label="Location *" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} />
          </div>
          <Field label="Employment Type" value={editing.employment_type} onChange={(v) => setEditing({ ...editing, employment_type: v })} />
          <Field label="Summary *" textarea value={editing.summary} onChange={(v) => setEditing({ ...editing, summary: v })} />
          <Field label="Responsibilities (one per line)" textarea value={editing.responsibilities} onChange={(v) => setEditing({ ...editing, responsibilities: v })} />
          <Field label="Requirements (one per line)" textarea value={editing.requirements} onChange={(v) => setEditing({ ...editing, requirements: v })} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_open} onChange={(e) => setEditing({ ...editing, is_open: e.target.checked })} /> Open for applications</label>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Insights CRUD ---------- */
const emptyInsight = { title: "", slug: "", category: "Point of View", author: "Eazymuv Editorial", summary: "", body: "", featured_image: "", tags: "", seo_title: "", seo_description: "", published: true };
function Insights() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/insights?all=true").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing, tags: typeof editing.tags === "string" ? editing.tags.split(",").map((s) => s.trim()).filter(Boolean) : editing.tags };
    try {
      if (editing.id) await api.put(`/admin/insights/${editing.id}`, payload);
      else await api.post("/admin/insights", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/insights/${id}`); toast.success("Deleted"); load(); };
  const edit = (a) => setEditing({ ...a, tags: (a.tags || []).join(", ") });

  return (
    <div data-testid="admin-insights">
      <button onClick={() => setEditing({ ...emptyInsight })} data-testid="new-insight-btn" className="mb-6 inline-flex items-center gap-2 bg-ez-text text-ez-bg px-5 py-2.5 font-semibold text-sm hover:bg-ez-yellow hover:text-ez-text transition-colors"><Plus size={16} weight="bold" /> New Insight</button>
      <div className="border border-ez-text divide-y divide-ez-border">
        {items.map((a) => (
          <div key={a.id} className="p-5 flex items-center justify-between gap-4">
            <div>
              <div className="font-semibold">{a.title} {!a.published && <span className="text-xs text-ez-text/40">(draft)</span>}</div>
              <div className="text-xs text-ez-text/50">{a.category} · /{a.slug}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => edit(a)} className="p-2 hover:bg-ez-bg"><PencilSimple size={16} /></button>
              <button onClick={() => del(a.id)} data-testid={`del-insight-${a.id}`} className="p-2 text-red-500 hover:bg-ez-bg"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title={editing.id ? "Edit Insight" : "New Insight"} onClose={() => setEditing(null)} onSubmit={save} testid="insight-form">
          <Field label="Title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v, slug: editing.id ? editing.slug : v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Slug *" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
            <Field label="Category *" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
          </div>
          <Field label="Author *" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} />
          <ImageUpload label="Featured Image" value={editing.featured_image} onChange={(v) => setEditing({ ...editing, featured_image: v })} />
          <Field label="Featured Image URL (or paste a link)" value={editing.featured_image} onChange={(v) => setEditing({ ...editing, featured_image: v })} />
          <Field label="Summary *" textarea value={editing.summary} onChange={(v) => setEditing({ ...editing, summary: v })} />
          <Field label="Body *" textarea rows={6} value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} />
          <Field label="Tags (comma separated)" value={editing.tags} onChange={(v) => setEditing({ ...editing, tags: v })} />
          <Field label="SEO Title" value={editing.seo_title} onChange={(v) => setEditing({ ...editing, seo_title: v })} />
          <Field label="SEO Description" value={editing.seo_description} onChange={(v) => setEditing({ ...editing, seo_description: v })} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published</label>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Team CRUD ---------- */
const emptyMember = { name: "", role: "", tag: "", note: "", image_url: "", linkedin: "", order: 0, visible: true };
function Team() {
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/team").then((r) => setMembers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/team/${editing.id}`, payload);
      else await api.post("/admin/team", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/team/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="admin-team">
      <button onClick={() => setEditing({ ...emptyMember, order: members.length + 1 })} data-testid="new-member-btn" className="mb-6 inline-flex items-center gap-2 bg-ez-text text-ez-bg px-5 py-2.5 font-semibold text-sm hover:bg-ez-yellow hover:text-ez-text transition-colors"><Plus size={16} weight="bold" /> New Team Member</button>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ez-border border border-ez-text">
        {members.map((m) => (
          <div key={m.id} className="bg-white p-5 flex items-center gap-4">
            <div className="w-14 h-14 shrink-0 bg-ez-bg border border-ez-border overflow-hidden flex items-center justify-center">
              {m.image_url ? <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" /> : <span className="text-ez-text/30 text-xs">no img</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{m.name} {!m.visible && <span className="text-xs text-ez-text/40">(hidden)</span>}</div>
              <div className="text-xs text-ez-text/50 truncate">{m.role}</div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEditing({ ...m })} className="p-2 hover:bg-ez-bg"><PencilSimple size={16} /></button>
              <button onClick={() => del(m.id)} data-testid={`del-member-${m.id}`} className="p-2 text-red-500 hover:bg-ez-bg"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title={editing.id ? "Edit Member" : "New Member"} onClose={() => setEditing(null)} onSubmit={save} testid="member-form">
          <ImageUpload label="Photo" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} />
          <Field label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
          <Field label="Role / Designation *" value={editing.role} onChange={(v) => setEditing({ ...editing, role: v })} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Tag (e.g. Founder)" value={editing.tag} onChange={(v) => setEditing({ ...editing, tag: v })} />
            <Field label="Display order" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} />
          </div>
          <Field label="LinkedIn URL" value={editing.linkedin} onChange={(v) => setEditing({ ...editing, linkedin: v })} />
          <Field label="Short note / bio" textarea value={editing.note} onChange={(v) => setEditing({ ...editing, note: v })} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} /> Visible on website</label>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Clients CRUD ---------- */
const emptyClient = { name: "", logo_url: "", website: "", category: "managed", order: 0, visible: true };
function Clients() {
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/clients").then((r) => setClients(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/clients/${editing.id}`, payload);
      else await api.post("/admin/clients", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/clients/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="admin-clients">
      <div className="mb-4 text-sm text-ez-text/60 max-w-2xl">Add your customers with names and logos. Only add logos you have permission to display. They appear in the "Our Customers" section on the site.</div>
      <button onClick={() => setEditing({ ...emptyClient, order: clients.length + 1 })} data-testid="new-client-btn" className="mb-6 inline-flex items-center gap-2 bg-ez-text text-ez-bg px-5 py-2.5 font-semibold text-sm hover:bg-ez-yellow hover:text-ez-text transition-colors"><Plus size={16} weight="bold" /> New Client</button>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ez-border border border-ez-text">
        {clients.map((cl) => (
          <div key={cl.id} className="bg-white p-5 flex items-center gap-4">
            <div className="w-16 h-12 shrink-0 bg-ez-bg border border-ez-border overflow-hidden flex items-center justify-center">
              {cl.logo_url ? <img src={cl.logo_url} alt={cl.name} className="max-w-full max-h-full object-contain" /> : <span className="text-ez-text/30 text-[10px]">no logo</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{cl.name} {!cl.visible && <span className="text-xs text-ez-text/40">(hidden)</span>}</div>
              <div className="text-xs text-ez-text/50 truncate">{cl.website || "Customer"}</div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEditing({ ...cl })} className="p-2 hover:bg-ez-bg"><PencilSimple size={16} /></button>
              <button onClick={() => del(cl.id)} data-testid={`del-client-${cl.id}`} className="p-2 text-red-500 hover:bg-ez-bg"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title={editing.id ? "Edit Client" : "New Client"} onClose={() => setEditing(null)} onSubmit={save} testid="client-form">
          <ImageUpload label="Logo" value={editing.logo_url} onChange={(v) => setEditing({ ...editing, logo_url: v })} />
          <Field label="Client name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
          <Field label="Website URL" value={editing.website} onChange={(v) => setEditing({ ...editing, website: v })} />
          <Field label="Display order" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} /> Visible on website</label>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Shared form bits ---------- */
function ImageUpload({ label, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="block text-[11px] font-mono-ez uppercase tracking-wider text-ez-text/50 mb-1">{label}</label>
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 bg-ez-bg border border-ez-text overflow-hidden flex items-center justify-center shrink-0">
          {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <span className="text-ez-text/30 text-[10px]">none</span>}
        </div>
        <label className="inline-flex items-center gap-2 border border-ez-text px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-ez-bg transition-colors" data-testid="image-upload">
          <UploadSimple size={16} /> {busy ? "Uploading…" : "Upload image"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy} />
        </label>
        {value && <button type="button" onClick={() => onChange("")} className="text-xs text-red-500">Remove</button>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, textarea, rows = 3 }) {
  return (
    <div>
      <label className="block text-[11px] font-mono-ez uppercase tracking-wider text-ez-text/50 mb-1">{label}</label>
      {textarea ? (
        <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-ez-text px-3 py-2 text-sm outline-none resize-none focus:ring-2 focus:ring-ez-yellow ring-inset" />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-ez-text px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ez-yellow ring-inset" />
      )}
    </div>
  );
}
function Modal({ title, onClose, onSubmit, children, testid }) {
  return (
    <div className="fixed inset-0 z-[60] bg-os-bg/70 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={onSubmit} data-testid={testid} className="bg-white border border-ez-text w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-ez-text px-6 py-4 sticky top-0 bg-ez-bg">
          <div className="font-display text-lg uppercase tracking-tight">{title}</div>
          <button type="button" onClick={onClose}><X size={22} /></button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
        <div className="border-t border-ez-text px-6 py-4 sticky bottom-0 bg-ez-bg">
          <button type="submit" data-testid="modal-save" className="bg-ez-text text-ez-bg px-6 py-2.5 font-semibold hover:bg-ez-yellow hover:text-ez-text transition-colors">Save</button>
        </div>
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-ez-bg">
      <SEO title="Admin Dashboard" description="Eazymuv admin" />
      <header className="bg-os-bg text-white border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="font-display text-xl uppercase tracking-tighter flex items-center gap-3"><img src="/eazymuv-logo.jpeg" alt="Eazymuv" className="h-7 w-auto" /> <span className="text-white/50 text-sm font-mono-ez normal-case tracking-normal">Admin</span></div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-white/60">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm border border-white/20 px-4 py-2 hover:bg-white/10 transition-colors"><SignOut size={16} /> Logout</button>
          </div>
        </div>
      </header>
      <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-8">
        <div className="flex flex-wrap gap-px bg-ez-text border border-ez-text w-fit mb-8">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-colors ${tab === t.id ? "bg-ez-yellow text-ez-text" : "bg-ez-bg text-ez-text hover:bg-white"}`}>
              <t.i size={16} /> {t.label}
            </button>
          ))}
        </div>
        {tab === "overview" && <Overview />}
        {tab === "leads" && <Leads />}
        {tab === "applications" && <Applications />}
        {tab === "jobs" && <Jobs />}
        {tab === "insights" && <Insights />}
        {tab === "team" && <Team />}
        {tab === "clients" && <Clients />}
      </div>
    </div>
  );
}
